import React from 'react';
import { graphql } from 'gatsby';
import ProductDetailMasthead from './ProductDetailMasthead';
import ProductHeroV2 from '../ProductHeroV2';
import {
    KontentRadioMultiChoice,
    BrandColor,
    KontentNumberValue,
    KontentStringValue,
    KontentMedia,
    KontentStringCodenameValue,
    KontentRichText,
    CTAConfiguration,
} from '../../../types/KontentProps';

export type ContentPosition = 'left' | 'center' | 'right';
export type VerticalPosition = 'top' | 'center' | 'bottom';
export interface ProductDetailProps extends CTAConfiguration {
    product_name_position: KontentRadioMultiChoice<ContentPosition>;
    text_background_color: BrandColor;
    text_background_opacity: KontentNumberValue;
    under_image_bar_color: BrandColor;
    under_image_bar_show_hide: KontentRadioMultiChoice<'show' | 'hide'>;
    display_headline: KontentStringValue;
    display_sub_headline?: KontentStringValue;
    general_large___small_image_selection__large: { value: any[] };
    general_large___small_image_selection__small: KontentMedia;
    tablet_image: KontentMedia;
    vertical_alignment: KontentRadioMultiChoice<VerticalPosition>;
    display_headline_tag: KontentStringCodenameValue;
    display_sub_headline_tag?: KontentStringCodenameValue;
    description_copy?: KontentStringValue;
    description_tag?: KontentStringCodenameValue;
    product_hero_version?: KontentRadioMultiChoice<'version_1' | 'version_2'>;
    product_statements?: {
        value: {
            elements: {
                headline: KontentStringValue;
                description: KontentRichText;
            };
        }[];
    };
    media_logos?: KontentMedia;
    media_logos_title?: KontentStringValue;
    kontentItemId?: string;
    font_color?: BrandColor;
}

const ProductDetailMastheadContainer: React.FC<ProductDetailProps> = ({
    ...props
}) => {
    const isVersionTwo =
        props?.product_hero_version?.value[0]?.codename === 'version_2';

    if (isVersionTwo) {
        // Map ProductDetailMasthead props to ProductHeroV2 props
        const heroV2Props = {
            product_name: props.display_headline,
            product_description: props.description_copy || '',
            product_subtitle: props.display_sub_headline || '',
            cta_label: props.cta_configuration__cta_label || '',
            gallery_images:
                props.general_large___small_image_selection__large?.value?.slice(
                    -4
                ) || [],
            feature_blocks:
                props.product_statements?.value?.map((statement) => ({
                    title: statement.elements.headline,
                    description: statement.elements.description,
                })) || [],
            recognition_title:
                props.media_logos_title?.value || 'Newsworthy recognition:',
            recognition_stars: 5,
            media_logos: props.media_logos?.value || [],
            cta_location:
                props.cta_configuration__cta_location?.value?.[0]?.elements
                    ?.slug?.url ||
                props.cta_configuration__cta_location?.value?.[0]?.elements
                    ?.third_party_url?.value ||
                props.cta_configuration__cta_location?.value?.[0]?.elements
                    ?.file?.value?.[0]?.url,
            cta_window_action:
                props.cta_configuration__cta_window_action?.value?.[0]
                    ?.codename,
            cta_location_anchor_point:
                props.cta_configuration__cta_location_anchor_point?.value,
        };

        return (
            <ProductHeroV2
                kontentItemId={props.kontentItemId}
                {...heroV2Props}
            />
        );
    }

    return <ProductDetailMasthead {...props} />;
};

export default ProductDetailMastheadContainer;

export const fragmentProductDetailMasthead = graphql`
    fragment ProductDetailMasthead on kontent_item_component___product_detail_masthead {
        elements {
            product_name_position {
                value {
                    codename
                }
            }
            product_hero_version {
                value {
                    codename
                }
            }
            cta_configuration__cta_label {
                value
            }
            cta_configuration__cta_location {
                ...kontentCtaLocation
            }
            cta_configuration__cta_window_action {
                value {
                    codename
                }
            }
            cta_configuration__cta_location_anchor_point {
                value
            }
            text_background_color {
                ...kontentColor
            }
            text_background_opacity {
                value
            }
            font_color {
                ...kontentColor
            }
            under_image_bar_color {
                name
            }
            under_image_bar_show_hide {
                value {
                    codename
                }
            }
            display_headline {
                value
            }
            display_sub_headline_tag {
                value {
                    name
                    codename
                }
            }
            display_headline_tag {
                value {
                    name
                    codename
                }
            }
            display_sub_headline_tag {
                value {
                    codename
                    name
                }
            }
            description_copy {
                value
            }
            description_tag {
                value {
                    codename
                    name
                }
            }
            general_large___small_image_selection__large {
                ...kontentImage
            }
            general_large___small_image_selection__small {
                ...kontentImage
            }
            tablet_image {
                ...kontentImage
            }
            vertical_alignment {
                value {
                    codename
                }
            }
            product_statements {
                value {
                    ... on kontent_item_content_item___product_statement {
                        id
                        elements {
                            headline {
                                value
                            }
                            description {
                                value
                            }
                        }
                    }
                }
            }
            media_logos {
                ...kontentImage
            }
            media_logos_title {
                value
            }
        }
    }
`;
